import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { usePlanLabel } from '../i18n/usePlanLabel';
import { appTheme } from '../styles/theme';

const floatingStyles = css`
  position: fixed;
  z-index: 100;
  border-radius: ${appTheme.radiusPill};
  border: 1px solid ${appTheme.border};
  background: rgba(250, 250, 248, 0.94);
  backdrop-filter: blur(12px);
  box-shadow: ${appTheme.shadowMd};
`;

const Chip = styled.div`
  ${floatingStyles}
  top: auto;
  right: 12px;
  bottom: 24px;
  left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
`;

const Avatar = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
`;

const Initial = styled.span`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${appTheme.green};
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
`;

const NameLink = styled(Link)`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${appTheme.text};
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-decoration: none;

  &:hover {
    color: ${appTheme.green};
    text-decoration: underline;
  }
`;

const PlanLink = styled(Link)`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${appTheme.green};
  background: ${appTheme.mint};
  border-radius: ${appTheme.radiusPill};
  padding: 3px 8px;
  text-decoration: none;

  &:hover {
    background: #dceedf;
  }
`;

const SignOutButton = styled.button`
  border: none;
  background: none;
  padding: 2px 6px;
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 700;
  color: ${appTheme.textMuted};
  cursor: pointer;

  &:hover {
    color: ${appTheme.text};
    text-decoration: underline;
  }
`;

const SignInButton = styled.button`
  ${floatingStyles}
  top: auto;
  right: 12px;
  bottom: 24px;
  left: auto;
  padding: 6px 14px;
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 700;
  color: ${appTheme.green};
  cursor: pointer;

  &:hover {
    background: ${appTheme.mint};
  }
`;

/** Routes without the global AppHeader, where the floating chip is needed. */
const FLOATING_ROUTES = new Set(['/preview', '/edit']);

const AccountChip: React.FC = () => {
  const { t } = useTranslation();
  const { user, logout, openLogin } = useAuth();
  const { pathname } = useLocation();
  const planLabel = usePlanLabel(user && user !== null ? user.plan : null, true);

  if (!FLOATING_ROUTES.has(pathname) || user === undefined) {
    return null;
  }

  if (user === null) {
    return (
      <SignInButton type="button" onClick={() => openLogin()}>
        {t('account.signIn')}
      </SignInButton>
    );
  }

  return (
    <Chip>
      {user.avatarUrl ? (
        <Avatar src={user.avatarUrl} alt="" referrerPolicy="no-referrer" />
      ) : (
        <Initial>{user.name.charAt(0).toUpperCase()}</Initial>
      )}
      <NameLink to="/profile" title={t('account.profileSettings')}>
        {user.name}
      </NameLink>
      <PlanLink to="/subscription" title={t('account.manageSubscription')}>
        {planLabel}
      </PlanLink>
      <SignOutButton type="button" onClick={() => logout()}>
        {t('account.signOut')}
      </SignOutButton>
    </Chip>
  );
};

export default AccountChip;
